import React, { useState, useEffect } from 'react';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import { useUserContext } from 'common/contexts/UserContext';
import { Calendars } from 'components/CalendarSchedule';
import { useSegment } from 'common/hooks/segment';
import { Pack } from 'types/Billing';
import { PaymentMethodType } from 'components/PaymentForm';
import { checkForPermission, Permission, usePermissions } from 'utils/permissions';
import CheckoutStatus from './CheckoutStatus';
import { getPacks } from '../../../services/clients/subscription';
import PlansPage from './PlansPage';
import CheckoutPage from './CheckoutPage';
import DowngradeRequest from './DowngradeRequest';
import { BUTTON_ACTIONS, CHECKOUT_STEPS } from './constants';

const PlansAndBillingPage = () => {
  const [packs, setPacks] = useState<Pack[]>([]);
  const [selectedPack, setSelectedPack] = useState<Pack>();
  const [step, setStep] = useState(CHECKOUT_STEPS.DISPLAY_PLANS);
  const { user } = useUserContext();
  const { trackEvent } = useSegment();
  const permissions = usePermissions();

  useEffect(() => {
    const fetchPacks = async () => {
      setPacks(await getPacks());
    };
    fetchPacks();
  }, []);

  if (checkForPermission([Permission.PlanAndPricingContactSales], permissions)) {
    return (
      <div className="p-8 py-14">
        <DowngradeRequest />
      </div>
    );
  }

  const handlePackSelected = (packId: Pack['id'], textButton: Pack['buttonText']) => {
    const selected = packs.find((p) => p.id === packId);

    trackEvent('Plan Clicked', {
      email: user?.email,
      firstName: user?.first_name,
      lastName: user?.last_name,
      phone: user?.phone_number,
      currentPlan: user?.pack,
      companyScac: user?.company_scac,
      packId: selected?.id,
      packName: selected?.displayName,
      textButton,
    });

    if (packId === 'bc_connect') {
      window.open(Calendars.ScheduleDemo, '_blank');
    } else {
      setSelectedPack(selected);
      setStep(CHECKOUT_STEPS.BUILD_PLAN);
    }
  };

  let subcomponent = <PlansPage packs={packs} user={user} onPackSelected={handlePackSelected} />;

  if (selectedPack !== undefined) {
    if ([CHECKOUT_STEPS.BUILD_PLAN, CHECKOUT_STEPS.PAYMENT_METHOD].includes(step)) {
      subcomponent =
        selectedPack.buttonText === BUTTON_ACTIONS.DOWNGRADE ? (
          <DowngradeRequest />
        ) : (
          <CheckoutPage
            selectedPack={selectedPack}
            onUpgrade={handlePackSelected}
            user={user}
            onCheckout={(method?: PaymentMethodType, isFreePlan = false) => {
              return isFreePlan ? setStep(step + 2) : setStep(step + 1);
            }}
            step={step}
          />
        );
    } else if (step === CHECKOUT_STEPS.CHECKOUT_STATUS) {
      subcomponent = (
        <CheckoutStatus selectedPack={selectedPack} onPackSelected={handlePackSelected} />
      );
    }
  }

  return (
    <div className="w-full h-full px-8 py-12">
      {[CHECKOUT_STEPS.BUILD_PLAN, CHECKOUT_STEPS.PAYMENT_METHOD].includes(step) && (
        <div
          className="flex space-x-2 items-center text-gray-700 font-regular cursor-pointer my-4"
          onClick={() => setStep(step - 1)}
        >
          <KeyboardBackspaceIcon />
          <h4>Back to {step === CHECKOUT_STEPS.BUILD_PLAN ? 'plans' : 'build your plan'}</h4>
        </div>
      )}
      {subcomponent}
    </div>
  );
};

export default PlansAndBillingPage;
